import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { SftpImportConfigurationWorkflow, ImportType, InventoryMode, DataType } from '../../../store/Models/Api/SftpImportModels';
import { AppServices } from '../../../AppServices';
import TextInput from '../../core/TextInput';
import { ReviewSection } from '../sections/ReviewSection';
import { ConnectionInfoSection } from '../sections/ConnectionInfoSection';
import { ScheduleInfoSection } from '../sections/ScheduleInfoSection';
import { ImportTypeInfoSection } from '../sections/ImportTypeInfoSection';
import { ProductIdentificationInfoSection } from '../sections/ProductIdentificationInfoSection';
import { DefaultHandlingInfoSection } from '../sections/DefaultHandlingInfoSection';
import { LibraryImportInfoSection } from '../sections/LibraryImportInfoSection';
import { LevelsConfigurationInfoSection } from '../sections/LevelsConfigurationInfoSection';
import { ColumnMappingDisplaySection } from '../sections/ColumnMappingDisplaySection';

interface ReviewStepLocalProps {
    goToPrevStep: () => void;
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
    onSave: () => void;
    connectionData?: {
        host: string;
        username: string;
        sshKey: string;
        directoryPath: string;
    } | null;
    isLimitReached?: boolean;
    configId?: string;
}

interface ReviewStepStateProps {
    appServices: AppServices;
}

type ReviewStepProps = ReviewStepLocalProps & ReviewStepStateProps & ToxnotComponentProperties;

class ReviewStepComponent extends ToxnotComponent<ReviewStepProps> {
    public isFormValid = () => {
        const isNameValid = this.props.configData.configurationName.trim() !== '';
        const { isLimitReached, configId } = this.props;
        
        // If it's a new configuration (no configId) and limit is reached, disable save
        if (!configId && isLimitReached) {
            return false;
        }
        
        return isNameValid;
    }

    public getSaveButtonText = () => {
        const { isLimitReached, configId } = this.props;
        
        if (!configId && isLimitReached) {
            return this.localizeString("Limit Reached (5/5)");
        }
        
        return this.localizeString("Save Configuration");
    }

    public render() {
        const { configData, onConfigChange, connectionData, appServices } = this.props;

        return (
            <div>
                <h3>{this.localizeString("Review Configuration")}</h3>
                <p>{this.localizeString("Please review the settings below before saving the configuration.")}</p>

                <div className="grouped-data">
                    <ReviewSection title={this.localizeString("SFTP Configuration Name")}>
                        <TextInput 
                            value={configData.configurationName} 
                            onValueChange={(value) => onConfigChange({ configurationName: value })} 
                        />
                    </ReviewSection>

                    <ReviewSection title={this.localizeString("Connection & Authentication")}>
                        <ConnectionInfoSection 
                            connectionData={connectionData}
                            subPath={configData.subPath}
                            fileName={configData.fileName}
                        />
                    </ReviewSection>

                    <ReviewSection title={this.localizeString("File Handling & Scheduling")}>
                        <ImportTypeInfoSection configData={configData} />
                    </ReviewSection>

                    {configData.inventoryMode === InventoryMode.One && configData.importType !== ImportType.Suppliers && (
                        <ReviewSection title={this.localizeString("Identifying Information")}>
                            <ProductIdentificationInfoSection configData={configData} />
                        </ReviewSection>
                    )}

                    {(configData.importType === ImportType.Suppliers || configData.dataType === DataType.Bom) && (
                        <ReviewSection title={this.localizeString("Default Handling")}>
                            <DefaultHandlingInfoSection configData={configData} />
                        </ReviewSection>
                    )}

                    {configData.importType !== ImportType.Suppliers && configData.inventoryMode === InventoryMode.Multiple && configData.dataType === DataType.Bom && (
                        <ReviewSection title={this.localizeString("Import Library Settings")}>
                            <LibraryImportInfoSection configData={configData} />
                        </ReviewSection>
                    )}

                    {configData.importType !== ImportType.Suppliers && configData.dataType === DataType.Bom && (
                        <ReviewSection title={this.localizeString("Configure Levels")}>
                            <LevelsConfigurationInfoSection configData={configData} />
                        </ReviewSection>
                    )}

                    <ReviewSection title={this.localizeString("Schedule and Post-Import Handling")}>
                        <ScheduleInfoSection configData={configData} />
                    </ReviewSection>

                    {configData.dataType !== DataType.Summary && configData.dataType !== DataType.Scip && configData.dataType !== DataType.ExternalDbIdUpdate && (
                        <ReviewSection title={this.localizeString("Column Mappings")}>
                            <ColumnMappingDisplaySection 
                                configData={configData}
                                isReadOnly={true}
                            />
                        </ReviewSection>
                    )}
                </div>

                {!this.props.configId && this.props.isLimitReached && (
                    <div className="alert alert-warning" style={{ marginTop: '15px' }}>
                        {this.localizeString("You have reached the maximum limit of 5 SFTP configurations. Please delete an existing configuration before creating a new one.")}
                    </div>
                )}

                <div className="text-right" style={{ marginTop: '15px' }}>
                    <button className="btn btn-default" onClick={this.props.goToPrevStep}>{this.localizeString("Back")}</button>
                    <button className="btn btn-toxnot-primary" style={{ marginLeft: "10px" }} onClick={this.props.onSave} disabled={!this.isFormValid()}>{this.getSaveButtonText()}</button>
                </div>
            </div>
        );
    }
}

const ReviewStep = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ReviewStepComponent);

export default ReviewStep;
